import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import { Avatar, Dialog, IconButton } from "@mui/material";
import React, { useEffect, useMemo, useState } from "react";
import { useMatchCommentary } from "../../../common/contexts/aiCommentaryContext";
import { getRelativeTimePl } from "../../../utils/timeUtils";
import { signatureColor } from "./characterColors";
import "./Commentary.scss";

// Themed shimmer placeholder shown while a match's commentary loads.
const CommentarySkeleton: React.FC = () => (
  <div className="commentarySkeleton" aria-hidden>
    <span className="commentarySkeleton__line commentarySkeleton__line--name" />
    <span className="commentarySkeleton__line" />
    <span className="commentarySkeleton__line" />
    <span className="commentarySkeleton__line commentarySkeleton__line--short" />
  </div>
);

interface CommentaryModalProps {
  open: boolean;
  onClose: () => void;
  matchId: number;
  homeName: string;
  awayName: string;
}

const characterImage = (slug: string) => `/images/characters/${slug}.png`;

// Every summary ends with the pundit's predicted score. The backend prompt asks
// for "**Typ eksperta - 2:1**" (dash after the label), but we accept either a dash
// or a colon there defensively. Pull it out of the prose so we can render it as a
// highlighted gold pill instead of leaving raw markdown asterisks in the text.
// (`**` made optional defensively.)
const TIP_RE = /\*{0,2}\s*Typ eksperta\s*[:-]\s*(\d+)\s*[:-]\s*(\d+)\s*\*{0,2}/i;

function splitTip(content: string): { body: string; tip: string | null } {
  const match = content.match(TIP_RE);
  if (!match) return { body: content, tip: null };
  return {
    body: content.replace(match[0], "").trimEnd(),
    tip: `${match[1]} : ${match[2]}`,
  };
}

const initials = (name: string) =>
  name
    .split(" ")
    .map((part) => part[0])
    .filter(Boolean)
    .slice(0, 2)
    .join("")
    .toUpperCase();

// Avatar picker + the selected pundit's commentary for a single match.
// Single-select: exactly one persona is highlighted at a time.
const CommentaryModal: React.FC<CommentaryModalProps> = ({
  open,
  onClose,
  matchId,
  homeName,
  awayName,
}) => {
  const { characters, analyses, loading } = useMatchCommentary(matchId);
  const [selectedSlug, setSelectedSlug] = useState<string | null>(null);

  const analysisBySlug = useMemo(
    () => new Map(analyses.map((a) => [a.characterSlug, a])),
    [analyses]
  );

  // Default to the first persona that actually has commentary for this match
  // (falling back to the first persona), and keep a valid selection thereafter.
  useEffect(() => {
    if (!open || characters.length === 0) return;
    if (selectedSlug && characters.some((c) => c.slug === selectedSlug)) return;
    const firstWithContent = characters.find((c) => analysisBySlug.has(c.slug));
    setSelectedSlug(firstWithContent?.slug ?? characters[0].slug);
  }, [open, characters, analysisBySlug, selectedSlug]);

  const selectedCharacter = characters.find((c) => c.slug === selectedSlug);
  const selectedAnalysis = selectedSlug
    ? analysisBySlug.get(selectedSlug)
    : undefined;
  const tipParts = selectedAnalysis ? splitTip(selectedAnalysis.content) : null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="xs"
      PaperProps={{ className: "commentaryModal__paper" }}
      // The Dialog portals to <body> in the DOM, but React synthetic events still
      // bubble through the component tree to the fixture tile's onClick (which
      // opens the bet modal). Stop them here so clicks inside the modal stay put.
      onClick={(e) => e.stopPropagation()}
    >
      <div className="commentaryModal__header">
        <div className="commentaryModal__heading">
          <span className="commentaryModal__kicker">AI Studio</span>
          <span className="commentaryModal__match">
            {homeName} vs {awayName}
          </span>
        </div>
        <IconButton
          aria-label="Close"
          onClick={onClose}
          className="commentaryModal__close"
          size="small"
        >
          <CloseRoundedIcon />
        </IconButton>
      </div>

      <div className="commentaryModal__avatars" role="group" aria-label="Pundits">
        {characters.map((character) => {
          const isSelected = character.slug === selectedSlug;
          const hasContent = analysisBySlug.has(character.slug);
          return (
            <button
              type="button"
              key={character.slug}
              className={`commentaryAvatar${
                isSelected ? " commentaryAvatar--selected" : ""
              }${hasContent ? "" : " commentaryAvatar--empty"}`}
              style={
                { "--sig": signatureColor(character.slug) } as React.CSSProperties
              }
              aria-pressed={isSelected}
              aria-label={character.name}
              onClick={() => setSelectedSlug(character.slug)}
            >
              <span className="commentaryAvatar__imgWrap">
                <Avatar
                  src={characterImage(character.slug)}
                  className="commentaryAvatar__img"
                >
                  {initials(character.name)}
                </Avatar>
                {isSelected && (
                  <CheckCircleRoundedIcon className="commentaryAvatar__check" />
                )}
              </span>
              <span className="commentaryAvatar__name">{character.name}</span>
            </button>
          );
        })}
      </div>

      <div className="commentaryModal__content">
        {loading ? (
          <CommentarySkeleton />
        ) : selectedAnalysis ? (
          // key={selectedSlug} remounts the panel on each switch so the
          // fade-in animation replays (no-op under prefers-reduced-motion).
          <div className="commentarySpeech" key={selectedSlug}>
            <div className="commentarySpeech__head">
              <span
                className="commentarySpeech__name"
                style={{ color: signatureColor(selectedAnalysis.characterSlug) }}
              >
                {selectedCharacter?.name ?? selectedAnalysis.characterName}
              </span>
              <span className="commentarySpeech__time">
                {getRelativeTimePl(selectedAnalysis.created)}
              </span>
            </div>
            <p className="commentarySpeech__text">
              {tipParts?.body ?? selectedAnalysis.content}
            </p>
            {tipParts?.tip && (
              <div className="commentaryTip">
                <span className="commentaryTip__label">Typ eksperta</span>
                <span className="commentaryTip__score">{tipParts.tip}</span>
              </div>
            )}
          </div>
        ) : (
          <p className="commentaryModal__empty">
            Ten ekspert jeszcze nie zabrał głosu w tym meczu.
          </p>
        )}
      </div>
    </Dialog>
  );
};

export default CommentaryModal;
